// @ts-nocheck

import { isWebDataSuccess, LegacyGeofenceRequester } from '@cobliteam/api-lib';
import GeofenceRequester from '@cobliteam/api-lib/src/dashboard/geofence/GeofenceRequester';
import type { Geofence, GeofenceValidationQuery, GeofenceValidationSummary } from '@cobliteam/common-types';
import { usePermissions } from '@cobliteam/permissions';

import { Environment } from '@cobliteam/react-environments';
import {
  deleteGeofenceSucceeded,
  importGeofencesFailed,
  importGeofencesSucceeded,
  loadGeofencesFailed,
  loadGeofencesSucceeded,
  saveGeofenceSucceeded,
  validateGeofencesFailed,
  validateGeofencesSucceeded,
} from '@cobliteam/store/src/Geofences/actionCreators';
import type { DeleteGeofenceRequest, EditGeofenceRequest, ImportGeofencesRequest, SaveGeofenceRequest, ValidateGeofencesRequest } from '@cobliteam/store/src/Geofences/actions';
import { ActionTypes } from '@cobliteam/store/src/Geofences/actionTypes';
import { selectValidAddresses } from '@cobliteam/store/src/Geofences/selectors';
import { showGeofences } from '@cobliteam/store/src/GeofenceTab';
import { SpreadsheetActionTypes } from '@cobliteam/store/src/HOR';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { toast } from 'sonner';

import { parseSpreadsheet } from '../HOR/Spreadsheet/sagas';

const env = Environment.getInstance().getEnvironment();

const legacyGeofenceRequester = new LegacyGeofenceRequester({
  host: env.herbieApiHost,
});
const geofenceRequester = new GeofenceRequester({ host: env.apiV2 });

export function* requestDeleteGeofenceSaga(action: DeleteGeofenceRequest) {
  const { geofence, fleetId } = action.payload;

  try {
    yield call(geofenceRequester.delete, geofence, fleetId);
    yield put(deleteGeofenceSucceeded(geofence.id));
    toast.success('Local de interesse excluído');
  } catch (err) {
    toast.error('Falha ao remover locais de interesse. Tente novamente mais tarde');
  }
}

export function* requestSaveGeofenceSaga(action: SaveGeofenceRequest) {
  const geofence = action.payload;

  try {
    const response: Geofence = yield call(geofenceRequester.save, geofence);
    yield put(saveGeofenceSucceeded(response));
    yield put(showGeofences());
    toast.success('Local de interesse adicionado');
  } catch (err) {
    toast.error('Falha ao salvar locais de interesse. Tente novamente mais tarde.');
  }
}

export function* requestLoadGeofencesSaga() {
  const { isAccessAllowed } = usePermissions();

  try {
    if (isAccessAllowed('view:places-of-interest')) {
      const geofences: Geofence[] = yield call(geofenceRequester.load);
      yield put(loadGeofencesSucceeded(geofences));
    } else {
      yield put(loadGeofencesFailed());
    }
  } catch (err) {
    yield put(loadGeofencesFailed());
    toast.error('Falha ao listar locais de interesse. Tente novamente mais tarde.');
  }
}

export function* requestEditGeofenceSaga(action: EditGeofenceRequest) {
  try {
    const { geofence, fleetId } = action.payload;
    if (geofence.geojson) {
      const newGeofence: Geofence = yield call(geofenceRequester.edit, geofence, fleetId);
      // we're doing the delete then save to make it go to the first item in the list without performing a new request
      yield put(deleteGeofenceSucceeded(geofence.id));
      yield put(saveGeofenceSucceeded(newGeofence));
      toast.success('Local de interesse editado');
    }
  } catch (err) {
    toast.error('Falha ao editar local de interesse. Tente novamente mais tarde.');
  }
}

export function* requestValidateGeofencesSagas(action: ValidateGeofencesRequest) {
  try {
    const summary: GeofenceValidationSummary = yield call(legacyGeofenceRequester.validate, action.payload);
    yield put(validateGeofencesSucceeded(summary));
  } catch (err) {
    toast.error('Ops! Houve um erro ao validar os endereços da planilha. Por favor, tente novamente.');
    yield put(validateGeofencesFailed());
  }
}

export function* requestImportGeofencesSagas(action: ImportGeofencesRequest) {
  try {
    yield call(legacyGeofenceRequester.import, action.payload);
    const validAddresses: WebData<GeofenceValidationQuery[]> = yield select(selectValidAddresses);
    yield put(importGeofencesSucceeded());
    const geofences: Geofence[] = yield call(geofenceRequester.load);
    yield put(loadGeofencesSucceeded(geofences));
    yield put(showGeofences());
    const numPlaces = isWebDataSuccess<GeofenceValidationQuery[]>(validAddresses) ? validAddresses.data.length : 0;
    const successMessage = `${numPlaces} ${numPlaces > 1 ? 'locais adicionados!' : 'local adicionado!'}`;
    toast.success(successMessage);
  } catch (err) {
    toast.error('Ops! Houve um erro ao importar os endereços da planilha. Por favor, tente novamente.');
    yield put(importGeofencesFailed());
  }
}

export function* GeofenceSagasListener() {
  yield all([
    takeLatest(ActionTypes.SAVE_GEOFENCE_REQUEST, requestSaveGeofenceSaga),
    takeLatest(ActionTypes.EDIT_GEOFENCE_REQUEST, requestEditGeofenceSaga),
    takeLatest(ActionTypes.LOAD_GEOFENCES_REQUEST, requestLoadGeofencesSaga),
    takeLatest(ActionTypes.DELETE_GEOFENCE_REQUEST, requestDeleteGeofenceSaga),
    takeLatest(ActionTypes.VALIDATE_GEOFENCES_REQUEST, requestValidateGeofencesSagas),
    takeLatest(ActionTypes.IMPORT_GEOFENCES_REQUEST, requestImportGeofencesSagas),
    takeLatest(SpreadsheetActionTypes.PARSE_SPREADSHEET_START, parseSpreadsheet),
  ]);
}
